import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";

type ToastKind = "success" | "error" | "info";

interface Toast {
    id: number;
    kind: ToastKind;
    message: string;
}

interface ToastContextValue {
    success: (message: string) => void;
    error: (message: string) => void;
    info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const styles: Record<ToastKind, { icon: React.ElementType; className: string }> = {
    success: { icon: CheckCircle2, className: "border-emerald-500/30 text-emerald-300" },
    error: { icon: AlertCircle, className: "border-red-500/30 text-red-300" },
    info: { icon: Info, className: "border-[#8b5cf6]/30 text-violet-200" },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const dismiss = useCallback((id: number) => {
        setToasts((list) => list.filter((t) => t.id !== id));
    }, []);

    const push = useCallback((kind: ToastKind, message: string) => {
        const id = Date.now() + Math.random();
        setToasts((list) => [...list.slice(-3), { id, kind, message }]);
        setTimeout(() => dismiss(id), kind === "error" ? 6000 : 3500);
    }, [dismiss]);

    const value = useMemo(
        () => ({
            success: (m: string) => push("success", m),
            error: (m: string) => push("error", m),
            info: (m: string) => push("info", m),
        }),
        [push]
    );

    return (
        <ToastContext.Provider value={value}>
            {children}

            <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-[100] flex flex-col gap-2 sm:w-96 pointer-events-none">
                <AnimatePresence>
                    {toasts.map((t) => {
                        const { icon: Icon, className } = styles[t.kind];
                        return (
                            <motion.div
                                key={t.id}
                                layout
                                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, x: 40 }}
                                className={`pointer-events-auto flex items-start gap-3 p-4 rounded-2xl bg-slate-900/95 backdrop-blur-xl border shadow-2xl ${className}`}
                            >
                                <Icon size={18} className="shrink-0 mt-0.5" />
                                <p className="text-sm flex-1 text-slate-200">{t.message}</p>
                                <button
                                    onClick={() => dismiss(t.id)}
                                    className="text-slate-500 hover:text-white transition-colors"
                                    aria-label="Dismiss"
                                >
                                    <X size={16} />
                                </button>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>
        </ToastContext.Provider>
    );
}

export function useToast(): ToastContextValue {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
    return ctx;
}